import { FormControl } from '@angular/forms';
import { FormlyExtension, FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';

type MaterialFormFieldState = {
  _control?: {
    stateChanges?: Subject<void>;
  };
};

type FieldWithMaterialFormField = FormlyFieldConfig & {
  _formField?: MaterialFormFieldState;
};

const observedControls = new WeakSet<FormControl>();

/**
 * Angular Material recomputes a form-field's disabled styling only when its
 * control emits `stateChanges`. Formly updates the Angular control when schema
 * conditions change but its Material adapter does not emit that notification.
 *
 * Native Material controls such as `MatInput` emit this notification
 * themselves. Radio and multi-checkbox fields have no nested
 * `MatFormFieldControl`, so Formly uses its fallback Material field control.
 * That fallback exposes the current disabled state but does not notify
 * Material when it changes. A field first rendered disabled by a conditional
 * schema expression can therefore have an enabled `FormControl` while its
 * Material container still blocks pointer events.
 */
export const materialControlStateExtension: FormlyExtension = {
  postPopulate(field: FormlyFieldConfig): void {
    if (!(field.formControl instanceof FormControl)) {
      return;
    }

    const control = field.formControl;
    if (observedControls.has(control)) {
      return;
    }

    observedControls.add(control);
    control.registerOnDisabledChange(() => {
      Promise.resolve().then(() => {
        const formField = (field as FieldWithMaterialFormField)._formField;
        formField?._control?.stateChanges?.next();
      });
    });
  },
};
